import { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Clock, MapPin, Phone, Mail, Calendar, ArrowRight, 
  Check, Star, Shield, Sparkles, X
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';

const API = process.env.REACT_APP_BACKEND_URL;

export default function OfferLandingPage() {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const [landing, setLanding] = useState(null);
  const [loading, setLoading] = useState(true);
  const [visitorId, setVisitorId] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState(null); // 'appointment' or 'contact'
  const [submitting, setSubmitting] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    message: '',
    date: '',
    time: ''
  });

  useEffect(() => {
    fetchLanding();
  }, [slug]);

  const fetchLanding = async () => {
    try {
      const source = searchParams.get('source') || 'direct';
      const campaign = searchParams.get('campaign') || '';
      
      const res = await axios.get(`${API}/api/landings/public/${slug}?source=${source}&campaign=${campaign}`);
      setLanding(res.data.landing);
      setVisitorId(res.data.visitor_id);
      
      // Guardar visitor_id en localStorage
      localStorage.setItem('lg_visitor', res.data.visitor_id);
    } catch (error) {
      console.error('Error loading landing:', error);
    } finally {
      setLoading(false);
    }
  };

  const trackClick = async () => {
    if (!visitorId) return;
    try {
      await axios.post(`${API}/api/landings/public/${slug}/track-click`, {
        visitor_id: visitorId
      });
    } catch (e) {
      console.error('Track click error:', e);
    }
  };

  const handleCTA = () => {
    trackClick();
    
    if (landing.cta_type === 'book_appointment') {
      setModalType('appointment');
      setShowModal(true);
    } else if (landing.cta_type === 'contact' || landing.cta_type === 'reserve') {
      setModalType('contact');
      setShowModal(true);
    } else if (landing.cta_link) {
      window.open(landing.cta_link, '_blank');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      if (modalType === 'appointment') {
        await axios.post(`${API}/api/landings/public/${slug}/book-appointment`, {
          date: formData.date,
          time: formData.time,
          customer_name: formData.name,
          customer_phone: formData.phone,
          customer_email: formData.email,
          notes: formData.message
        });
        toast.success('¡Cita reservada! Te contactaremos para confirmar.');
      } else {
        await axios.post(`${API}/api/landings/public/${slug}/contact`, {
          name: formData.name,
          phone: formData.phone,
          email: formData.email,
          message: formData.message
        });
        toast.success('¡Mensaje enviado! Te contactaremos pronto.');
      }
      
      setShowModal(false);
      setFormData({ name: '', phone: '', email: '', message: '', date: '', time: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al enviar');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-500" />
      </div>
    );
  }

  if (!landing) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-800 mb-2">Oferta no encontrada</h1>
          <p className="text-gray-600">Esta oferta ya no está disponible o el enlace es incorrecto.</p>
        </div>
      </div>
    );
  }

  const themeColor = landing.theme_color || '#4A7C59';
  const daysLeft = landing.expires_at 
    ? Math.max(0, Math.ceil((new Date(landing.expires_at) - new Date()) / (1000 * 60 * 60 * 24)))
    : null;

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {landing.business_data.logo_url ? (
              <img 
                src={landing.business_data.logo_url} 
                alt={landing.business_data.name}
                className="w-10 h-10 rounded-lg object-cover"
              />
            ) : (
              <div 
                className="w-10 h-10 rounded-lg flex items-center justify-center text-white font-bold"
                style={{ backgroundColor: themeColor }}
              >
                {landing.business_data.name?.charAt(0) || 'N'}
              </div>
            )}
            <div>
              <h2 className="font-semibold text-gray-800">{landing.business_data.name}</h2>
              <p className="text-xs text-gray-500 capitalize">{landing.business_data.business_type}</p>
            </div>
          </div>
          
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <Shield className="w-4 h-4" />
            <span>Verificado por LokAlGrow</span>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-2xl shadow-xl overflow-hidden"
        >
          {/* Image */}
          {landing.image_url && (
            <div className="relative h-64 md:h-80">
              <img 
                src={landing.image_url} 
                alt={landing.title}
                className="w-full h-full object-cover"
              />
              {landing.offer_type === 'discount' && landing.offer_value && (
                <div 
                  className="absolute top-4 right-4 px-4 py-2 rounded-full text-white font-bold text-lg"
                  style={{ backgroundColor: themeColor }}
                >
                  -{landing.offer_value}%
                </div>
              )}
            </div>
          )}

          {/* Content */}
          <div className="p-6 md:p-8">
            {/* Urgency Banner */}
            {daysLeft !== null && daysLeft <= 7 && (
              <div className="mb-6 bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-center gap-3">
                <Clock className="w-5 h-5 text-amber-600" />
                <span className="text-amber-800 font-medium">
                  {daysLeft === 0 ? '¡Último día!' : `Quedan ${daysLeft} día${daysLeft !== 1 ? 's' : ''}`}
                </span>
              </div>
            )}

            {/* Title & Description */}
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              {landing.title}
            </h1>

            {/* Price/Value */}
            {landing.offer_value && (
              <div className="flex items-baseline gap-3 mb-6">
                {landing.offer_type === 'discount' ? (
                  <>
                    {landing.offer_original_price && (
                      <span className="text-2xl text-gray-400 line-through">
                        {landing.offer_original_price}€
                      </span>
                    )}
                    <span className="text-4xl font-bold" style={{ color: themeColor }}>
                      {landing.offer_original_price 
                        ? Math.round(landing.offer_original_price * (1 - landing.offer_value / 100))
                        : landing.offer_value}€
                    </span>
                  </>
                ) : (
                  <span className="text-4xl font-bold" style={{ color: themeColor }}>
                    {landing.offer_value}€
                  </span>
                )}
              </div>
            )}

            <p className="text-gray-600 text-lg leading-relaxed mb-8 whitespace-pre-line">
              {landing.description}
            </p>

            {/* Benefits */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
              <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-xl">
                <Check className="w-5 h-5" style={{ color: themeColor }} />
                <span className="text-gray-700">Reserva garantizada</span>
              </div>
              <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-xl">
                <Star className="w-5 h-5" style={{ color: themeColor }} />
                <span className="text-gray-700">Negocio verificado</span>
              </div>
              <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-xl">
                <Sparkles className="w-5 h-5" style={{ color: themeColor }} />
                <span className="text-gray-700">Oferta exclusiva</span>
              </div>
            </div>

            {/* CTA Button */}
            <Button
              onClick={handleCTA}
              className="w-full py-6 text-xl font-semibold text-white rounded-xl transition-transform hover:scale-[1.02]"
              style={{ backgroundColor: themeColor }}
              data-testid="landing-cta-btn"
            >
              {landing.cta_text}
              <ArrowRight className="w-6 h-6 ml-2" />
            </Button>

            {/* Business Contact */}
            <div className="mt-8 pt-6 border-t border-gray-100">
              <p className="text-sm text-gray-500 mb-3">Contacta directamente:</p>
              <div className="flex flex-wrap gap-4">
                {landing.business_data.phone && (
                  <a 
                    href={`tel:${landing.business_data.phone}`}
                    className="flex items-center gap-2 text-gray-600 hover:text-gray-900"
                  >
                    <Phone className="w-4 h-4" />
                    {landing.business_data.phone}
                  </a>
                )}
                {landing.business_data.email && (
                  <a 
                    href={`mailto:${landing.business_data.email}`}
                    className="flex items-center gap-2 text-gray-600 hover:text-gray-900"
                  >
                    <Mail className="w-4 h-4" />
                    {landing.business_data.email}
                  </a>
                )}
                {landing.business_data.address && (
                  <span className="flex items-center gap-2 text-gray-600">
                    <MapPin className="w-4 h-4" />
                    {landing.business_data.address}
                  </span>
                )}
              </div>
            </div>
          </div>
        </motion.div>

        {/* Footer */}
        <div className="mt-8 text-center text-sm text-gray-400">
          <p>Oferta gestionada por LokAlGrow</p>
        </div>
      </main>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-white rounded-2xl w-full max-w-md p-6"
          >
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-gray-900">
                {modalType === 'appointment' ? 'Reservar Cita' : 'Contactar'}
              </h3>
              <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nombre *</label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  placeholder="Tu nombre"
                  data-testid="form-name"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Teléfono *</label>
                <Input
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  required
                  placeholder="+34 600 000 000"
                  data-testid="form-phone"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="tu@email.com"
                  data-testid="form-email"
                />
              </div>

              {modalType === 'appointment' && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Fecha *</label>
                    <Input
                      type="date"
                      value={formData.date}
                      onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                      required
                      min={new Date().toISOString().split('T')[0]}
                      data-testid="form-date"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Hora *</label>
                    <Input
                      type="time"
                      value={formData.time}
                      onChange={(e) => setFormData({ ...formData, time: e.target.value })}
                      required
                      data-testid="form-time"
                    />
                  </div>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {modalType === 'appointment' ? 'Notas (opcional)' : 'Mensaje'}
                </label>
                <textarea
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                  rows={3}
                  placeholder={modalType === 'appointment' ? 'Alguna preferencia o nota...' : 'Tu mensaje...'}
                  data-testid="form-message"
                />
              </div>

              <Button
                type="submit"
                disabled={submitting}
                className="w-full py-3 text-white font-semibold"
                style={{ backgroundColor: themeColor }}
                data-testid="form-submit"
              >
                {submitting ? 'Enviando...' : (modalType === 'appointment' ? 'Reservar Cita' : 'Enviar Mensaje')}
              </Button>
            </form>
          </motion.div>
        </div>
      )}
    </div>
  );
}
